import { Stack, Typography } from "@mui/material";
import { DataGridPro, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid-pro";
import { type FC, type ReactElement, useMemo } from "react";
import {
  filterStatType,
  type GroupedMomentAnalyticsData,
} from "../../../../modules/stats/server/analytics-service";
import type { ISO8601 } from "../../../../utils/iso8601";
import { AnalyticsDatetime } from "../../utils/analytics-datetime";
import { ExportToolbar } from "../generic/export-toolbar";

type PageView = {
  first_name: string;
  last_name: string;
  visits: number;
  first_visit: ISO8601 | null;
  most_recent_visit: ISO8601 | null;
};

export const AnalyticsSendsTableExpandedPageViews: FC<{
  stats: GroupedMomentAnalyticsData["stats"];
}> = ({ stats }) => {
  const rows = useMemo(() => {
    const pageViewsByRecipient = new Map<string, PageView>();

    filterStatType("click", stats).forEach(({ data }) => {
      const key =
        data.recipient_data?.person_id ??
        data.recipient_data?.destination ??
        data.recipient_data?.id ??
        "unknown";
      const existing = pageViewsByRecipient.get(key);

      pageViewsByRecipient.set(key, {
        first_name: data.recipient_data?.first_name ?? "-",
        last_name: data.recipient_data?.last_name ?? "-",
        visits: (existing?.visits ?? 0) + data.count,
        first_visit:
          !existing?.first_visit ||
          (data.min_timestamp !== null && data.min_timestamp < existing.first_visit)
            ? data.min_timestamp
            : existing.first_visit,
        most_recent_visit:
          !existing?.most_recent_visit ||
          (data.max_timestamp !== null && data.max_timestamp > existing.most_recent_visit)
            ? data.max_timestamp
            : existing.most_recent_visit,
      });
    });

    return Array.from(pageViewsByRecipient.values()).map((pageView, id) => ({
      id,
      ...pageView,
    }));
  }, [stats]);

  const columns = useMemo(() => {
    const renderDatetime = (
      params: GridRenderCellParams<(typeof rows)[0], ISO8601>,
    ): ReactElement | null =>
      params.value ? (
        <Stack direction="row" spacing={1} alignItems="center" height="100%">
          <AnalyticsDatetime datetime={params.value} />
        </Stack>
      ) : null;

    return [
      {
        field: "first_name",
        headerName: "First name",
        flex: 1,
      },
      {
        field: "last_name",
        headerName: "Last name",
        flex: 1,
      },
      {
        field: "visits",
        headerName: "Visits",
        flex: 0.5,
      },
      {
        field: "first_visit",
        headerName: "First visit",
        flex: 1.5,
        renderCell: renderDatetime,
      },
      {
        field: "most_recent_visit",
        headerName: "Most recent visit",
        flex: 1.5,
        renderCell: renderDatetime,
      },
    ] satisfies GridColDef<(typeof rows)[0]>[];
  }, []);

  const totalPageViews = rows.reduce((total, row) => total + row.visits, 0);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="baseline" spacing={2}>
        <Typography variant="h4">{totalPageViews}</Typography>
        <Typography variant="body2" color="text.secondary">
          Page views from {rows.length} unique {rows.length === 1 ? "visitor" : "visitors"}
        </Typography>
      </Stack>
      <DataGridPro
        pagination
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        slots={{ toolbar: ExportToolbar }}
        showToolbar
      />
    </Stack>
  );
};
